import {
  Button,
  Form,
  InputNumber,
  Modal,
  Result,
  Select,
  Table,
  Typography,
  notification,
} from "antd";
import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import useApi from "../../utils/useApi";

const { Title, Text } = Typography;

const FIELD_LABELS = {
  conversao_pct: "Conversão (%)",
  rentabilidade_m2: "Rentabilidade (R$/m²/mês)",
  permanencia_min: "Permanência mín. (min)",
  rpm: "Receita/pessoa-min (R$/min)",
};

export default function RScoreBenchmarksManager() {
  const { user } = useAuth();
  const {
    getRScoreAvailableTipos,
    getRScoreBenchmarks,
    createRScoreBenchmark,
    updateRScoreBenchmark,
  } = useApi();

  const [benchmarks, setBenchmarks] = useState([]);
  const [availableTipos, setAvailableTipos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const isSuperAdmin = Boolean(user?.is_superuser);

  const load = async () => {
    setLoading(true);
    try {
      const [data, tipos] = await Promise.all([
        getRScoreBenchmarks(),
        getRScoreAvailableTipos(),
      ]);
      setBenchmarks(Array.isArray(data) ? data : []);
      setAvailableTipos(Array.isArray(tipos) ? tipos : []);
    } catch {
      notification.error({ message: "Erro ao carregar benchmarks." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) load();
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return (
      <Result
        status="403"
        title="Acesso restrito"
        subTitle="Esta área é exclusiva para Super Admins."
      />
    );
  }

  const existingTipos = new Set(benchmarks.map((b) => b.tipo));
  const tiposOptions = availableTipos
    .filter((t) => !existingTipos.has(t))
    .map((t) => ({ label: t, value: t }));

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateRScoreBenchmark(editing.id, values);
        notification.success({ message: "Benchmark atualizado." });
      } else {
        await createRScoreBenchmark(values);
        notification.success({ message: "Benchmark criado." });
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      notification.error({
        message: "Erro ao salvar benchmark.",
        description: err?.response?.data?.tipo?.[0] || err?.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "Tipo de cliente",
      dataIndex: "tipo",
      key: "tipo",
      render: (v) => <Text strong>{v}</Text>,
    },
    ...Object.entries(FIELD_LABELS).map(([key, label]) => ({
      title: label,
      dataIndex: key,
      key,
      align: "right",
      render: (v) => v?.toFixed(2),
    })),
    {
      title: "Ações",
      key: "actions",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Button type="link" onClick={() => openEdit(record)}>
          Editar
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}>
        <Title level={4} style={{ margin: 0 }}>
          Benchmarks rSCORE por tipo de cliente
        </Title>
        <Button
          type="primary"
          onClick={openCreate}
          disabled={tiposOptions.length === 0}>
          Novo tipo
        </Button>
      </div>
      <Table
        dataSource={benchmarks}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="middle"
      />
      <Modal
        open={modalOpen}
        title={
          editing ? `Editar benchmark — ${editing.tipo}` : "Novo benchmark"
        }
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={editing ? "Salvar" : "Criar"}
        confirmLoading={saving}
        destroyOnClose>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {!editing && (
            <Form.Item
              name="tipo"
              label="Tipo de cliente"
              rules={[{ required: true, message: "Selecione o tipo." }]}>
              <Select
                options={tiposOptions}
                placeholder="Selecione um tipo cadastrado"
              />
            </Form.Item>
          )}
          {Object.entries(FIELD_LABELS).map(([key, label]) => (
            <Form.Item
              key={key}
              name={key}
              label={label}
              rules={[{ required: true, message: `Informe ${label}.` }]}>
              <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
            </Form.Item>
          ))}
        </Form>
      </Modal>
    </div>
  );
}
